'use client';

import { ReactNode, useMemo, useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import CourseMeta from '@/components/course-meta/course-meta';
import LessonsSidebar from '@/components/sidebars/lessons-sidebar';
import { CoursesRightSidebar } from '@/components/sidebars/courses-sidebar';
import LessonNavigation from '@/components/lessons/lesson-navigation';
import useUserCourseWithLessons from '@/hooks/useUserCourseWithLessons';
import { useAutoNext } from '@/hooks/useAutoNext';
import { Course, CourseLesson, VideoData } from '@/types/course';
import VideoPlayer from '../common/video-player';
import { fetchLearningPathProgress } from '@/lib/api/get-user-learning-paths';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { useLoginPrompt } from '@/hooks/auth/common/useLoginPrompt';
import { useLoginPromptStore } from '@/store/login-prompt-store';
import { LoginPromptModal } from '@/components/Login/common/LoginPromptModal';
import { useLoginModals } from '@/components/Login/common/LoginModalsManager';
import { usePartnerOverlay } from '@/hooks/usePartnerOverlay';
import BlurOverlay, { PartnerOnlyMessage } from '@/components/common/blur-overlay';
import { handleLearningPath, handleCourse } from '@/lib/api/update-user-activity';
import { getCourseThumbnailSrc, getCourseThumbnailUrl, hasCourseThumbnail } from '@/lib/utils/thumbnail-url';
import RelatedContent from '../sidebars/related-content';
import type { MarkdownExportSpec } from '@/lib/utils/markdown-export-path';

// Simplified course type for learning path navigation
interface LearningPathCourse {
  url: string;
  title?: string;
  uid?: string;
  chapters?: {
    lessons?: {
      lessons?: CourseLesson[];
    };
  }[];
}

interface LearningPathSidebarData {
  title?: string;
  url: string;
  uid?: string;
  courses?: LearningPathCourse[];
}

interface CourseClientWrapperProps {
  course: Course;
  courseId: string;
  duration: string;
  tabsContent: ReactNode;
  videoData: VideoData;
  lessons: CourseLesson[];
  isLessonVideo: boolean;
  lessonEntryUid?: string;
  lessonUrl?: string; // Current lesson URL for auto-next navigation
  sidebarContent?: ReactNode;
  learningPath?: string;
  learningPathData?: LearningPathSidebarData; // Learning path data for sidebar
  learningPathCourses?: LearningPathCourse[]; // All courses in the learning path
  showAssessment?: boolean;
  isStandalone?: boolean; // Flag for standalone content (no lessons sidebar)
  hasTextLesson?: boolean; // True when lesson has text content but no video
}

export default function CourseClientWrapper({
  course,
  courseId,
  duration,
  tabsContent,
  lessons,
  videoData,
  lessonEntryUid,
  lessonUrl,
  isLessonVideo,
  sidebarContent,
  learningPath,
  learningPathData,
  learningPathCourses,
  showAssessment = false,
  isStandalone = false,
  hasTextLesson,
}: CourseClientWrapperProps) {
  const queryClient = useQueryClient();

  // Check if content is partner-specific and show overlay if necessary
  const { overlayState: showPartnerOverlay, handleLogout } = usePartnerOverlay({
    isPartnerContent: course?.is_partner_specific,
  });
  const { openLoginModalWithPartnerRole } = useLoginModals();

  const { userCourseData, isLoading, userLessonData } = useUserCourseWithLessons({
    courseEntryUid: course?.uid,
    lessons: lessons,
    lessonEntryUid: lessonEntryUid,
  });

  // See useAuthUser()'s doc comment for why this must be reactive, not a one-shot read.
  const { isLoggedIn } = useAuthUser();

  // Track video playing state
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  // Store video player controls
  const [videoControls, setVideoControls] = useState<{
    play: () => void;
    pause: () => void;
  } | null>(null);

  // Track if we've already triggered hasStarted for this course view
  const hasStartedTriggered = useRef(false);

  // Login prompt for unauthenticated users: courses and learning paths only; not for stand-alone videos
  const { isPromptOpen, promptType, dismissPrompt, handleLogin, shouldBlockContent } =
    useLoginPrompt({
      autoTrack: !isStandalone,
      courseId: isStandalone ? undefined : (course?.uid ?? courseId),
    });

  const closePrompt = useLoginPromptStore((s) => s.closePrompt);

  // On standalone content: never show login prompt; close any stale prompt from previous navigation
  useEffect(() => {
    if (isStandalone) {
      closePrompt();
    }
  }, [isStandalone, closePrompt]);

  // Fetch user progress for all courses in the learning path
  const { data: learningPathUserData } = useQuery({
    queryKey: ['learning-path-user-data', learningPathData?.uid],
    queryFn: () => fetchLearningPathProgress(learningPathData!.uid!),
    enabled: isLoggedIn && !!learningPathData?.uid,
    staleTime: 0, // Always refetch to get latest bookmark state
    refetchOnMount: true,
  });

  // Build user courses data for the sidebar (includes bookmark status)
  const userCoursesData = useMemo(() => {
    if (!learningPathUserData?.courses) {
      return undefined;
    }
    return learningPathUserData.courses.map((uc) => ({
      entryUid: uc.entryUid,
      isBookmarked: uc.isBookmarked,
      progress: uc.progress,
    }));
  }, [learningPathUserData]);

  // Auto-next navigation hook
  const { goToNextLesson, nextLesson, isLastLesson } = useAutoNext({
    lessons: lessons || [],
    currentLessonUrl: lessonUrl || '',
    courseId,
    courseEntryUid: course?.uid,
    currentLessonEntryUid: lessonEntryUid,
    learningPath,
    learningPathCourses,
    learningPathHasAssessment: showAssessment,
  });

  // Find current lesson from lessons array
  const currentLesson = useMemo(() => {
    if (!lessonUrl || !lessons || lessons.length === 0) return null;
    return lessons.find((lesson) => lesson.url === lessonUrl) || null;
  }, [lessonUrl, lessons]);

  const markdownExportSpec = useMemo((): MarkdownExportSpec | null => {
    const seg = (s: string) => s.replace(/^\//, '').replace(/\/$/, '');
    const cid = seg(courseId);
    if (!cid) return null;
    if (isStandalone) {
      return { kind: 'content', contentId: cid };
    }
    const lp = learningPath ? seg(learningPath) : '';
    const lessonSeg = lessonUrl ? seg(lessonUrl) : '';
    if (lp) {
      if (lessonSeg) {
        return {
          kind: 'learning_path_lesson',
          learningPath: lp,
          courseId: cid,
          lessonId: lessonSeg,
        };
      }
      return { kind: 'learning_path_course', learningPath: lp, courseId: cid };
    }
    if (lessonSeg) {
      return { kind: 'lesson', courseId: cid, lessonId: lessonSeg };
    }
    return { kind: 'course', courseId: cid };
  }, [courseId, isStandalone, learningPath, lessonUrl]);

  // Get next lesson title for transition screen
  const nextLessonTitle = useMemo(() => {
    if (nextLesson) return nextLesson.title;
    // If last lesson in course but there's a next course in learning path
    if (isLastLesson && learningPath && learningPathCourses) {
      const currentCourseIndex = learningPathCourses.findIndex(
        (course) => course.url === `/${courseId}`
      );
      if (currentCourseIndex !== -1 && currentCourseIndex < learningPathCourses.length - 1) {
        const nextCourse = learningPathCourses[currentCourseIndex + 1];
        const firstLesson = nextCourse.chapters?.[0]?.lessons?.lessons?.[0];
        return firstLesson?.title || 'Next Course';
      }
      // Final lesson of the path: no "Up next" overlay (same as standalone last lesson)
      return undefined;
    }
    return undefined;
  }, [nextLesson, isLastLesson, learningPath, learningPathCourses, courseId]);

  // Track course view and mark as started (page-level tracking)
  useEffect(() => {
    const trackCourseView = async () => {
      // Only trigger once per component mount
      if (hasStartedTriggered.current) return;

      // Only track if user is logged in
      if (!isLoggedIn) return;

      // Don't track if we don't have the necessary data yet
      if (!course?.uid) return;

      // Mark as triggered to prevent duplicate calls
      hasStartedTriggered.current = true;

      try {
        // Track course hasStarted (only if not already started and not completed)
        const courseHasStarted = userCourseData?.progress?.hasStarted ?? false;
        const courseIsCompleted = userCourseData?.progress?.isCompleted ?? false;

        if (!courseHasStarted && !courseIsCompleted) {
          await handleCourse({
            courseEntryUid: course.uid,
            courseActivity: { progress: { hasStarted: true } },
            queryClient,
          });
        }

        // Track learning path hasStarted (only if in a learning path context)
        if (learningPath && learningPathData?.uid) {
          const lpHasStarted = learningPathUserData?.progress?.hasStarted ?? false;
          const lpIsCompleted = learningPathUserData?.progress?.isCompleted ?? false;

          if (!lpHasStarted && !lpIsCompleted) {
            await handleLearningPath({
              learningPathUid: learningPathData.uid,
              learningPathActivity: { progress: { hasStarted: true } },
              queryClient,
            });
          }
        }
      } catch (error) {
        // Silently handle errors in progress tracking
        console.error('Error tracking course view:', error);
      }
    };

    trackCourseView();
  }, [
    isLoggedIn,
    course?.uid,
    learningPath,
    learningPathData?.uid,
    userCourseData?.progress,
    learningPathUserData?.progress,
  ]);

  // Show overlay if content is partner-only and user is not a partner
  if (showPartnerOverlay.show) {
    return (
      <BlurOverlay
        open={showPartnerOverlay.show}
        message={<PartnerOnlyMessage />}
        buttons={[
          {
            id: 'partner-login',
            title: 'Log In as Partner',
            variant: 'primary',
            onClick: async () => {
              await handleLogout();
              openLoginModalWithPartnerRole();
            },
          },
          {
            id: 'academy-home',
            title: 'Go to Academy Home',
            variant: 'secondary',
            redirectPath: '/academy',
          },
        ]}
      />
    );
  }

  return (
    <>
      {/* Login Prompt Modal for unauthenticated users - never show on standalone content */}
      {!isStandalone && isPromptOpen && promptType && (
        <LoginPromptModal
          isOpen={isPromptOpen}
          type={promptType}
          onClose={dismissPrompt}
          onLogin={handleLogin}
        />
      )}

      <div
        className={`section ${!isStandalone && shouldBlockContent ? 'pointer-events-none select-none' : ''}`}
      >
        <div className="flex flex-col items-start gap-8 lg:flex-row">
          <div className="w-full lg:w-[70%]">
            {/* Video or Thumbnail - hide image section when no video and no course thumbnail */}
            {!hasTextLesson && (
            <>
              {videoData && videoData?.sources && videoData?.sources?.length > 0 ? (
                <VideoPlayer
                  userCourseData={userCourseData}
                  userLessonData={userLessonData}
                  thumbnail={
                    videoData?.lesson_thumbnail?.url || getCourseThumbnailUrl(course) || undefined
                  }
                  transcriptions_captions={videoData?.transcriptions_captions || undefined}
                  isLessonVideo={isLessonVideo}
                  courseEntryUid={course?.uid}
                  lessonEntryUid={lessonEntryUid}
                  currentVideoData={videoData}
                  onVideoComplete={!isStandalone || learningPath ? goToNextLesson : undefined}
                  nextLessonTitle={nextLessonTitle}
                  courseName={course?.title}
                  onPlayStateChange={setIsVideoPlaying}
                  onPlayerReady={setVideoControls}
                />
              ) : hasCourseThumbnail(course) ? (
                <Image
                  src={getCourseThumbnailSrc(course)}
                  alt={course?.title ?? 'Course'}
                  width={500}
                  height={281}
                  className="h-full w-full object-cover"
                />
              ) : null}
            </>
            )}

            {/* Course Meta - receives both course and user data */}
            <CourseMeta
              course={course}
              duration={duration}
              userCourseData={userCourseData || undefined}
              currentLesson={currentLesson}
              userLessonData={userLessonData || undefined}
              markdownSpec={markdownExportSpec}
              copyUrl={(() => {
                const basePath = process.env.NEXT_PUBLIC_BASE_PATH || '/academy';
                const cleanBase = basePath.endsWith('/') ? basePath.slice(0, -1) : basePath;
                if (learningPath) {
                  const coursePath = `${cleanBase}/learning-paths/${learningPath}/${courseId}`;
                  return lessonUrl ? `${coursePath}/${lessonUrl.replace(/^\//, '')}` : coursePath;
                }
                if (isStandalone) {
                  return `${cleanBase}/content/${courseId}`;
                }
                const coursePath = `${cleanBase}/courses/${courseId}`;
                return lessonUrl ? `${coursePath}/${lessonUrl.replace(/^\//, '')}` : coursePath;
              })()}
            />

            {/* Tabs and Navigation */}
            <div className="flex">{tabsContent}</div>

            {/* Sidebars - shown below tabs on mobile/tablet, hidden on desktop */}
            <div className="mt-5 md:w-[50%] lg:hidden">
              {/* Standalone content sidebar (e.g., RelatedContent) */}
              {isStandalone ? (
                sidebarContent
              ) : (
                <>
                  {/* Learning Path Courses Sidebar */}
                  {learningPathData && (
                    <CoursesRightSidebar
                      title={learningPathData.title}
                      courses={learningPathData.courses || []}
                      eyebrow="Learning Path"
                      url={learningPathData.url}
                      courseUrl={courseId}
                      userCoursesData={userCoursesData}
                      showAssessment={showAssessment}
                      learningPath={learningPath}
                    />
                  )}
                  {/* Lessons Sidebar - only for courses with lessons */}
                  <LessonsSidebar
                    courseUid={course?.uid}
                    heading={course?.title}
                    eyebrow="Course"
                    courseUrl={
                      learningPath
                        ? `/learning-paths/${learningPath}/${courseId}`
                        : `/courses/${courseId}`
                    }
                    lessons={course?.chapters?.[0]?.lessons || []}
                    lessonUrl={lessonUrl}
                    userCourseData={userCourseData}
                    isLoading={isLoading}
                    learningPath={learningPath}
                    showAssessment={showAssessment}
                    isVideoPlaying={isVideoPlaying}
                    currentLessonUid={lessonEntryUid}
                    videoControls={videoControls}
                  />
                </>
              )}
            </div>

            {/* Lesson Navigation - shown for text lessons (no video) */}
            {lessonUrl &&
              (!videoData || !videoData?.sources || videoData?.sources?.length === 0) &&
              lessons &&
              lessons.length > 0 && (
                <LessonNavigation
                  lessons={lessons}
                  currentLessonUrl={lessonUrl}
                  courseId={courseId}
                  courseEntryUid={course?.uid}
                  currentLessonEntryUid={lessonEntryUid}
                  learningPath={learningPath}
                  learningPathCourses={learningPathCourses}
                  learningPathHasAssessment={showAssessment}
                />
              )}
          </div>

          {/* Sidebars - shown on right side on desktop only */}
          <div className="sticky top-20 hidden w-full lg:block lg:w-[30%]">
            {/* Standalone content sidebar (e.g., RelatedContent) */}
            {/* {isStandalone ? (
              <div className="mt-5">{sidebarContent}</div>
            ) : ( */}
            <>
              {/* Learning Path Courses Sidebar */}
              {learningPathData && (
                <CoursesRightSidebar
                  title={learningPathData.title}
                  courses={learningPathData.courses || []}
                  eyebrow="Learning Path"
                  url={learningPathData.url}
                  courseUrl={courseId}
                  userCoursesData={userCoursesData}
                  showAssessment={showAssessment}
                  learningPath={learningPath}
                />
              )}
              {/* Lessons Sidebar - only for courses with lessons */}
              {isStandalone ? (
                <div className="mt-5">{sidebarContent}</div>
              ) : (
                <>
                  {/* Learning Path Courses Sidebar */}
                  <LessonsSidebar
                    courseUid={course?.uid}
                    heading={course?.title}
                    eyebrow="Course"
                    courseUrl={
                      learningPath
                        ? `/learning-paths/${learningPath}/${courseId}`
                        : `/courses/${courseId}`
                    }
                    lessons={course?.chapters?.[0]?.lessons || []}
                    lessonUrl={lessonUrl}
                    userCourseData={userCourseData}
                    isLoading={isLoading}
                    learningPath={learningPath}
                    showAssessment={showAssessment}
                    isVideoPlaying={isVideoPlaying}
                    currentLessonUid={lessonEntryUid}
                    videoControls={videoControls}
                  />
                </>
              )}
            </>
            {/* )} */}
          </div>
        </div>
      </div>
    </>
  );
}
